// Lightweight SEO hook — no react-helmet dependency, just direct DOM writes on mount
// and cleanup on unmount so navigating back to a page without a title falls back to
// the app default. Handles title, meta description, canonical link, and optional
// LocalBusiness JSON-LD for listing pages.
import { useEffect } from "react";

const DEFAULT_TITLE = "find2near · Vocal for Local — services, shopping, jobs & more near you";
const DEFAULT_DESC = "One neighbourhood super-app for services, shopping, rentals, property, matrimonial, MNC jobs and local stores — with live map tracking.";
const SITE_URL = typeof window !== "undefined" ? window.location.origin : "https://find2near.com";

// Ensure a <meta name="..."> tag exists; return the element.
const ensureMeta = (attr, name) => {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  return el;
};

const ensureLink = (rel) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  return el;
};

/**
 * useSeo({ title, description, canonical, schema })
 * - title:       page title. Suffixed with "· find2near" if not already.
 * - description: 155-char blurb for search engines.
 * - canonical:   absolute or relative URL — leave undefined to use current path.
 * - schema:      optional JSON-LD object; injected as <script type="application/ld+json">.
 */
export function useSeo({ title, description, canonical, schema } = {}) {
  useEffect(() => {
    const finalTitle = title
      ? (title.includes("find2near") ? title : `${title} · find2near`)
      : DEFAULT_TITLE;
    const finalDesc = description || DEFAULT_DESC;
    const finalCanonical = canonical
      ? (canonical.startsWith("http") ? canonical : `${SITE_URL}${canonical}`)
      : `${SITE_URL}${window.location.pathname}`;

    const prevTitle = document.title;
    document.title = finalTitle;

    ensureMeta("name", "description").setAttribute("content", finalDesc);
    ensureMeta("property", "og:title").setAttribute("content", finalTitle);
    ensureMeta("property", "og:description").setAttribute("content", finalDesc);
    ensureMeta("property", "og:url").setAttribute("content", finalCanonical);
    ensureMeta("property", "og:type").setAttribute("content", "website");
    ensureMeta("name", "twitter:card").setAttribute("content", "summary_large_image");
    ensureMeta("name", "twitter:title").setAttribute("content", finalTitle);
    ensureMeta("name", "twitter:description").setAttribute("content", finalDesc);
    ensureLink("canonical").setAttribute("href", finalCanonical);

    let schemaEl = null;
    if (schema) {
      schemaEl = document.createElement("script");
      schemaEl.type = "application/ld+json";
      schemaEl.id = "seo-schema";
      schemaEl.textContent = JSON.stringify(schema);
      // Remove any prior schema before appending.
      document.head.querySelectorAll("script#seo-schema").forEach(s => s.remove());
      document.head.appendChild(schemaEl);
    }

    return () => {
      document.title = prevTitle;
      schemaEl?.remove?.();
    };
  }, [title, description, canonical, JSON.stringify(schema || null)]);
}
